import { produce } from 'immer';
import { map, isNil } from 'lodash/fp';
import {
    ADD_OPERATION_TO_STACK,
    CANCEL_OPERATION,
    CREATE_TRANSFORMATION,
    SELECT_OPERATION,
    START_OPERATION,
    UPDATE_TRANSFORM_OPERATION
} from 'app/store/operations/types.js';
import { extractError } from 'app/lib/error';
import { LOAD_PROJECT } from 'app/store/projects/types.js';
import {
    ADD_COLUMN,
    UPDATE_FORMULA,
    SELECT_COLUMN,
    TOGGLE_ALL_COLUMNS_VISIBILITY,
    SET_COLUMN_LIST,
    REBASE_COLUMN_LIST,
    CANCEL_FORMULA,
    VALIDATE_REMOVE_COLUMN,
    VALIDATE_RENAME_COLUMN,
    VALIDATE_APPLY_FORMULA,
    DISABLE_COLUMN_VISIBILITY,
    ENABLE_COLUMN_VISIBILITY,
    CREATE_FORMULA_OPERATION,
    SYNC_TO_INPUT_COLUMNS
} from './types.js';
import { createColumn } from './utils.js';

export const INITIAL_STATE = {
    selectedColumnIndex: null,
    columnList: null,
    initColumnList: null,
    isOutdated: false,
    isSaved: false,
    error: null
};

function findLastVisibleColumnBefore(index, columns) {
    let previousIndex = null;

    for (let i = index - 1; i >= 0; i--) {
        if (columns[i].visible) {
            previousIndex = i;
            break;
        }
    }

    return previousIndex;
}

export const formulaEditorReducer = produce((draft, action) => {
    switch (action.type) {
        case SET_COLUMN_LIST: {
            const { columnList, keepOpen } = action.payload;
            const { selectedColumnIndex } = draft;
            const columns = columnList ?? [];

            draft.error = null;
            draft.initColumnList = columnList;
            draft.columnList = columnList;
            draft.isOutdated = false;
            draft.isSaved = false;

            if (!keepOpen) {
                draft.selectedColumnIndex = null;
            } else if (!isNil(selectedColumnIndex) && selectedColumnIndex >= columns.length) {
                draft.selectedColumnIndex = findLastVisibleColumnBefore(columns.length, columns);
            }

            break;
        }

        case REBASE_COLUMN_LIST: {
            const { initColumnList, columnList } = action.payload;
            const selectedName = draft.columnList?.[draft.selectedColumnIndex]?.name;
            const selectedIndex = columnList.findIndex(({ name }) => name === selectedName);

            draft.initColumnList = initColumnList;
            draft.columnList = columnList;
            draft.isOutdated = false;
            draft.selectedColumnIndex = columnList[selectedIndex]?.visible ? selectedIndex : null;

            break;
        }

        case SELECT_COLUMN: {
            const index = action.payload.index;
            const column = draft.columnList?.[index];

            if (!isNil(column) && column.visible) {
                draft.selectedColumnIndex = index;
            }

            break;
        }

        case VALIDATE_RENAME_COLUMN.SUCCEEDED: {
            const { columnIndex, columnName } = action.meta;
            const column = draft.columnList?.[columnIndex];

            draft.error = null;

            if (isNil(column)) {
                break;
            }

            column.name = columnName;

            const localColumns = draft.columnList;
            const remoteColumns = action.payload?.columns;

            if (remoteColumns) {
                for (let li = 0, ri = 0; li < localColumns.length;) {
                    const column = localColumns[li++];
                    if (column.formula) {
                        column.formula = remoteColumns[ri++].expression;
                    }
                }
            }

            break;
        }

        case ADD_COLUMN: {
            const numColumns = draft.columnList.length;
            const names = map('name', draft.columnList);
            let i = numColumns + 1;
            let name;

            do {
                name = `COLUMN_${i++}`;
            } while (names.includes(name));

            draft.selectedColumnIndex = numColumns;
            draft.columnList.push(createColumn({ name }));

            break;
        }

        case SELECT_OPERATION:
        case START_OPERATION:
        case CANCEL_OPERATION:
            return INITIAL_STATE;

        case UPDATE_FORMULA: {
            const { index, formula } = action.payload;
            const column = draft.columnList?.[index];

            if (isNil(column)) {
                break;
            }

            const keepPending = !isNil(draft.error) && !isNil(column.pendingFormula);

            if (column.formula !== formula || keepPending) {
                column.pendingFormula = formula;
            } else {
                column.pendingFormula = null;
            }

            break;
        }

        case CANCEL_FORMULA: {
            const { index } = action.payload;
            const column = draft.columnList?.[index];
            const initColumn = draft.initColumnList?.[index];

            draft.error = null;

            if (isNil(column)) {
                break;
            }

            column.pendingFormula = null;

            if (!isNil(initColumn)) {
                column.formula = initColumn.formula;
            }

            break;
        }

        case VALIDATE_APPLY_FORMULA.SUCCEEDED: {
            const { index, formula } = action.meta;
            const column = draft.columnList?.[index];

            draft.error = null;

            if (isNil(column)) {
                break;
            }

            column.formula = formula;
            column.hasError = false;
            column.pendingFormula = null;
            break;
        }

        case CREATE_TRANSFORMATION.SUCCEEDED:
        case ADD_OPERATION_TO_STACK.SUCCEEDED:
        case UPDATE_TRANSFORM_OPERATION.SUCCEEDED:
            draft.isSaved = true;

            break;

        case LOAD_PROJECT.SUCCEEDED:
            if (isNil(draft.columnList)) {
                break;
            }

            if (draft.isSaved) {
                draft.columnList = null;
                draft.isSaved = false;
            } else {
                draft.isOutdated = true;
            }

            break;

        case ENABLE_COLUMN_VISIBILITY: {
            const index = action.payload.columnIndex;

            draft.columnList[index].visible = true;

            break;
        }

        case DISABLE_COLUMN_VISIBILITY: {
            const index = action.payload.columnIndex;

            draft.columnList[index].visible = false;

            if (draft.selectedColumnIndex === index) {
                draft.selectedColumnIndex = null;
            }

            break;
        }

        case TOGGLE_ALL_COLUMNS_VISIBILITY: {
            const { showAll } = action.payload;

            for (const column of draft.columnList) {
                column.visible = Boolean(showAll);
            }

            if (!showAll) {
                draft.selectedColumnIndex = null;
            }

            break;
        }

        case VALIDATE_REMOVE_COLUMN.SUCCEEDED: {
            const index = action.meta.columnIndex;

            draft.error = null;

            if (isNil(draft.columnList)) {
                break;
            }

            draft.columnList.splice(index, 1);

            if (index === draft.selectedColumnIndex) {
                draft.selectedColumnIndex = findLastVisibleColumnBefore(index, draft.columnList);
            }

            // if user deleted a middle column while the last column was selected
            if (draft.selectedColumnIndex >= draft.columnList.length) {
                draft.selectedColumnIndex = findLastVisibleColumnBefore(
                    draft.columnList.length,
                    draft.columnList
                );
            }

            break;
        }
        case SYNC_TO_INPUT_COLUMNS: {
            const indexes = [...action.payload.indexes].sort((a, b) => b - a);

            for (const index of indexes) {
                draft.columnList.splice(index, 1);

                if (index === draft.selectedColumnIndex) {
                    draft.selectedColumnIndex = findLastVisibleColumnBefore(
                        index,
                        draft.columnList
                    );
                } else if (index < draft.selectedColumnIndex) {
                    draft.selectedColumnIndex--;
                }
            }

            break;
        }
        case CREATE_TRANSFORMATION.FAILED:
        case CREATE_FORMULA_OPERATION.FAILED:
        case VALIDATE_REMOVE_COLUMN.FAILED:
        case VALIDATE_RENAME_COLUMN.FAILED:
        case VALIDATE_APPLY_FORMULA.FAILED: {
            draft.error = extractError(action.payload);
            break;
        }
        default:
    }
}, INITIAL_STATE);
